import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Todo, RecurrenceType, RecurrenceConfig, Group } from '@ext-bookmark/shared/types';
import { getRecurrenceTypeLabel } from '../../services/todo/recurrence';
import { useQueryClient } from '@tanstack/react-query';
import { saveTodo, deleteTodo, getTodos, deleteTodos } from '../../services/todo/storage';
import { markTodoUpdatedByUs } from '../../services/todo/lastTodoUpdateByUs';
import { useAuth } from '../../hooks/useAuth';
import { useGroupsQuery, useGroupMembersQuery } from '../../hooks/queries/useGroupQueries';
import { getLastUsedGroupId, setLastUsedGroupId, clearLastUsedGroupId } from '../../services/group/lastUsedGroup';
import { todoSchema, type TodoFormData } from '../../lib/validation-schemas';
import { FormError } from '../ui/form-error';

/** 无日期占位值：仅出现在待办视图，不参与日历格 */
const NO_DATE_START = 0;

interface TodoFormProps {
  todo?: Todo;
  defaultDate?: Date | null; // 新建时的默认日期；null 表示默认「无日期」
  defaultGroupId?: string | null; // 新建任务时的默认分组ID
  groups?: Group[]; // 从父组件传递的分组列表，避免重复加载
  onClose: () => void;
  onSave?: (todo: Todo, opts?: { deleted?: boolean }) => void;
  isMobile?: boolean;
}

const TodoForm: React.FC<TodoFormProps> = ({ todo, defaultDate, defaultGroupId, groups: propsGroups, onClose, onSave, isMobile = false }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: groupsFromQuery = [] } = useGroupsQuery({ enabled: !!user });
  const isEditMode = !!todo;
  
  // 原始待办事项（如果是重复事项的实例，需要加载原始事项）
  const [originalTodo, setOriginalTodo] = useState<Todo | undefined>(todo);
  
  // React Hook Form 管理所有表单字段
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset: _reset,
  } = useForm<TodoFormData>({
    resolver: zodResolver(todoSchema),
    defaultValues: {
      title: todo?.title || '',
      description: todo?.description || '',
      selectedGroupId: (todo?.groupId || defaultGroupId) ?? undefined,
      fontColor: todo?.fontColor || '#000000',
      recurrenceType: todo?.recurrence?.type,
      recurrenceFrequencyType: todo?.recurrence?.maxOccurrences ? 'count' : (todo?.endDate ? 'endDate' : 'unlimited'),
      endDate: todo?.endDate ? new Date(todo.endDate) : null,
      recurrenceCount: todo?.recurrence?.maxOccurrences || 10,
      reminderEnabled: todo?.reminder?.enabled || false,
      reminderMinutes: todo?.reminder?.minutesBefore || [],
      assignedTo: todo?.assignedTo || [],
    },
  });

  // 监听表单字段变化
  const _formTitle = watch('title');
  const _formDescription = watch('description');
  void _formTitle;
  void _formDescription;
  const formSelectedGroupId = watch('selectedGroupId');
  const formFontColor = watch('fontColor');
  const formRecurrenceType = watch('recurrenceType');
  const formRecurrenceFrequencyType = watch('recurrenceFrequencyType');
  const formEndDate = watch('endDate');
  const formRecurrenceCount = watch('recurrenceCount');
  const formReminderEnabled = watch('reminderEnabled');
  const formReminderMinutes = watch('reminderMinutes');
  const formAssignedTo = watch('assignedTo');

  const { data: groupMembersFromQuery = [] } = useGroupMembersQuery(
    formSelectedGroupId && typeof formSelectedGroupId === 'string' ? formSelectedGroupId : null
  );
  const groups = (propsGroups && propsGroups.length > 0 ? propsGroups : groupsFromQuery) as Group[];
  const groupMembers = groupMembersFromQuery;

  // 保持向后兼容的状态（用于复杂逻辑）
  const [_completed, setCompleted] = useState(todo?.completed || false);
  void setCompleted;
  
  // 使用表单状态（简化变量名，用于兼容现有代码）
  const selectedGroupId = formSelectedGroupId;
  const _fontColor = formFontColor || '#000000';
  const _recurrenceType = formRecurrenceType || 'daily';
  const _recurrenceFrequencyType = formRecurrenceFrequencyType || 'unlimited';
  const _endDate = formEndDate;
  const _recurrenceCount = formRecurrenceCount || 10;
  const _reminderMinutes = formReminderMinutes || [];
  const _assignedTo = formAssignedTo || [];
  void _fontColor;
  void _recurrenceType;
  void _recurrenceFrequencyType;
  void _endDate;
  void _recurrenceCount;
  void _reminderMinutes;
  void _assignedTo;
  // 开始日期：使用 defaultDate 或 todo.startDate；无日期时用 0，表单内用「无日期」选项
  const startDate = useMemo<Date>(() => {
    if (originalTodo && originalTodo.startDate !== NO_DATE_START) {
      return new Date(originalTodo.startDate);
    }
    if (todo && todo.startDate !== NO_DATE_START) {
      return new Date(todo.startDate);
    }
    if (defaultDate instanceof Date && !isNaN(defaultDate.getTime())) {
      return defaultDate;
    }
    return new Date();
  }, [originalTodo, todo, defaultDate]);

  // 是否选择「无日期」（仅待办视图可见，不显示在日历中）
  const [hasNoDate, setHasNoDate] = useState<boolean>(() =>
    !!(todo?.startDate === NO_DATE_START || originalTodo?.startDate === NO_DATE_START || defaultDate === null)
  );
  useEffect(() => {
    if (originalTodo?.startDate === NO_DATE_START) setHasNoDate(true);
  }, [originalTodo?.startDate]);

  // 表单中的开始日期（可编辑，用于日期选择器）
  const [formStartDate, setFormStartDate] = useState<Date>(() => startDate);
  useEffect(() => {
    setFormStartDate(startDate);
  }, [startDate]);
  
  // 重复配置状态（用于复杂逻辑）
  const [recurrenceConfig, setRecurrenceConfig] = useState<RecurrenceConfig | undefined>(
    todo?.recurrence
  );
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [_isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  void setIsLoadingOriginal;

  // 折叠区块：基本信息(展开)、重复与提醒(折叠)、高级选项(折叠)；编辑时若有非默认值则展开对应区块
  const [expandedBasic, setExpandedBasic] = useState(true);
  const [expandedRecurrenceReminder, setExpandedRecurrenceReminder] = useState(
    !!(todo?.recurrence || todo?.reminder?.enabled)
  );
  const [expandedAdvanced, setExpandedAdvanced] = useState(
    !!((todo?.assignedTo && todo.assignedTo.length > 0) || (todo?.fontColor && todo.fontColor !== '#000000'))
  );
  
  // 统一管理所有下拉列表的状态，确保同时只有一个打开
  const [openDropdown, setOpenDropdown] = useState<'group' | 'recurrence' | 'frequency' | 'color' | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  
  // 下拉列表按钮的 ref
  const groupButtonRef = useRef<HTMLButtonElement>(null);
  const recurrenceButtonRef = useRef<HTMLButtonElement>(null);
  const frequencyButtonRef = useRef<HTMLButtonElement>(null);
  
  // 下拉列表状态管理函数（使用 useCallback 优化）
  const toggleDropdown = useCallback((dropdown: 'group' | 'recurrence' | 'frequency' | 'color') => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null);
      setDropdownPosition(null);
    } else {
      setOpenDropdown(dropdown);
      // 计算下拉列表位置
      let buttonRef: React.RefObject<HTMLButtonElement> | null = null;
      if (dropdown === 'group') {
        buttonRef = groupButtonRef;
      } else if (dropdown === 'recurrence') {
        buttonRef = recurrenceButtonRef;
      } else if (dropdown === 'frequency') {
        buttonRef = frequencyButtonRef;
      }
      
      if (buttonRef?.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 200; // 估算下拉菜单高度
        
        // 计算位置，确保不超出屏幕
        let top = rect.bottom + 8;
        if (top + dropdownHeight > viewportHeight) {
          top = rect.top - dropdownHeight - 8;
        }
        
        setDropdownPosition({
          top: Math.max(8, top),
          left: rect.left,
          width: rect.width,
        });
      }
    }
  }, [openDropdown]);
  
  const closeAllDropdowns = useCallback(() => {
    setOpenDropdown(null);
    setDropdownPosition(null);
  }, []);
  const [deleteButtonRef, setDeleteButtonRef] = useState<HTMLButtonElement | null>(null);
  const [deleteMenuPosition, setDeleteMenuPosition] = useState<{ top: number; left: number } | null>(null);

  // 加载原始待办事项（如果是重复事项的实例）
  useEffect(() => {
    const loadOriginalTodo = async () => {
      if (!todo || !isEditMode) return;
      
      // 检查是否是重复事项的实例 ID（包含时间戳后缀）
      const lastUnderscoreIndex = todo.id.lastIndexOf('_');
      if (lastUnderscoreIndex > 0) {
        const afterLastUnderscore = todo.id.substring(lastUnderscoreIndex + 1);
        // 检查是否是纯数字（时间戳）
        if (/^\d+$/.test(afterLastUnderscore)) {
          const originalTodoId = todo.id.substring(0, lastUnderscoreIndex);
          
          setIsLoadingOriginal(true);
          try {
            const allTodos = await getTodos();
            const found = allTodos.find(t => t.id === originalTodoId);
            
            if (found) {
              console.log('[TodoForm] 加载原始待办事项:', {
                instanceId: todo.id,
                originalId: originalTodoId,
                originalStartDate: new Date(found.startDate).toISOString(),
                originalEndDate: found.endDate ? new Date(found.endDate).toISOString() : '无'
              });
              
              setOriginalTodo(found);
              
              // 使用原始事项的配置更新表单（使用 React Hook Form 的 setValue）
              setValue('title', found.title);
              setValue('description', found.description || '');
              setCompleted(found.completed);
              // startDate 不需要更新，因为它会在保存时使用 originalTodo.startDate
              setValue('endDate', found.endDate ? new Date(found.endDate) : null);
              setValue('recurrenceType', found.recurrence?.type || 'daily');
              setRecurrenceConfig(found.recurrence);
              // 设置重复频次类型
              if (found.recurrence?.maxOccurrences) {
                setValue('recurrenceFrequencyType', 'count');
                setValue('recurrenceCount', found.recurrence.maxOccurrences);
              } else if (found.endDate) {
                setValue('recurrenceFrequencyType', 'endDate');
              } else {
                setValue('recurrenceFrequencyType', 'unlimited');
              }
              setValue('reminderEnabled', found.reminder?.enabled || false);
              setValue('reminderMinutes', found.reminder?.minutesBefore || []);
              setValue('fontColor', found.fontColor || '#000000');
            }
          } catch (error) {
            console.error('[TodoForm] 加载原始待办事项失败:', error);
          } finally {
            setIsLoadingOriginal(false);
          }
        }
      }
    };
    
    loadOriginalTodo();
  }, [todo, isEditMode, setValue]);

  // 初始化分组选择：优先使用最近使用的分组，验证分组是否存在
  useEffect(() => {
    const initializeGroup = async () => {
      // 编辑模式：使用todo的分组ID，不需要改变
      if (todo?.groupId) {
        return;
      }

      // 新建模式：如果已经有defaultGroupId，使用它
      if (defaultGroupId) {
        return;
      }

      // 如果没有指定分组且分组列表已加载，尝试使用最近使用的分组
      if (groups.length > 0 && !formSelectedGroupId) {
        const lastUsedGroupId = await getLastUsedGroupId();
        
        if (lastUsedGroupId) {
          // 验证最近使用的分组是否还存在
          const groupExists = groups.some(g => g.id === lastUsedGroupId);
          if (groupExists) {
            setValue('selectedGroupId', lastUsedGroupId);
            return;
          } else {
            // 如果分组不存在，清除最近使用的分组记录
            await clearLastUsedGroupId();
          }
        }

        // 如果没有最近使用的分组或分组不存在，默认选择个人分组
        const personalGroup = groups.find(g => g.isPersonal);
        if (personalGroup) {
          setValue('selectedGroupId', personalGroup.id);
        } else if (groups.length > 0) {
          // 如果没有个人分组，选择第一个分组
          setValue('selectedGroupId', groups[0].id);
        }
      }
    };

    initializeGroup();
  }, [groups, todo?.groupId, defaultGroupId, formSelectedGroupId, setValue]);

  // 重复类型选项
  const recurrenceOptions: Array<{ value: RecurrenceType; label: string }> = [
    { value: 'daily', label: '日重复' },
    { value: 'weekday', label: '工作日重复(周一 ~ 周五)' },
    { value: 'weekly', label: '周重复(周五)' },
    { value: 'monthly', label: '月重复(2日)' },
    { value: 'monthly-last-day', label: '每月最后一天重复(31日)' },
    { value: 'quarterly', label: '每季度(1月2日)' },
    { value: 'yearly', label: '年重复(1月2日)' },
    { value: 'lunar-yearly', label: '农历年重复(十一月十四)' },
    { value: 'legal-workday', label: '法定工作日重复' },
    { value: 'ebbinghaus', label: '艾宾浩斯记忆曲线' },
    { value: 'custom-daily', label: '自定义日重复' },
    { value: 'custom-weekly', label: '自定义周重复' },
    { value: 'custom-monthly', label: '自定义月重复' },
    { value: 'custom-yearly', label: '自定义年重复' },
  ];

  // 预设颜色
  const presetColors = [
    '#000000', '#FF3B30', '#FF9500', '#FFCC00',
    '#34C759', '#5AC8FA', '#007AFF', '#5856D6',
    '#AF52DE', '#FF2D55', '#8E8E93',
  ];

  // 更新重复配置：仅当用户选择了重复类型时才维护 config，否则保持不重复
  useEffect(() => {
    if (formRecurrenceType == null || formRecurrenceType === undefined) {
      setRecurrenceConfig(undefined);
      return;
    }
    const newConfig: RecurrenceConfig = {
      type: formRecurrenceType,
    };

    switch (formRecurrenceType) {
      case 'weekly':
        // 如果已有配置，保留；否则使用开始日期的星期
        newConfig.weekday = recurrenceConfig?.weekday !== undefined 
          ? recurrenceConfig.weekday 
          : formStartDate.getDay();
        break;
      case 'monthly':
        newConfig.dayOfMonth = recurrenceConfig?.dayOfMonth !== undefined
          ? recurrenceConfig.dayOfMonth
          : formStartDate.getDate();
        break;
      case 'quarterly':
        newConfig.quarterDay = recurrenceConfig?.quarterDay || {
          month: formStartDate.getMonth(),
          day: formStartDate.getDate(),
        };
        break;
      case 'yearly':
        newConfig.yearDay = recurrenceConfig?.yearDay || {
          month: formStartDate.getMonth(),
          day: formStartDate.getDate(),
        };
        break;
      case 'lunar-yearly':
        // TODO: 实现农历转换
        newConfig.lunarMonth = recurrenceConfig?.lunarMonth || 1;
        newConfig.lunarDay = recurrenceConfig?.lunarDay || 1;
        break;
      case 'ebbinghaus':
        newConfig.ebbinghausStartDate = recurrenceConfig?.ebbinghausStartDate || formStartDate.getTime();
        newConfig.ebbinghausStages = recurrenceConfig?.ebbinghausStages || [1, 2, 4, 7, 15, 30, 60, 90]; // 默认艾宾浩斯曲线
        break;
      case 'custom-daily':
      case 'custom-weekly':
      case 'custom-monthly':
      case 'custom-yearly':
        newConfig.interval = recurrenceConfig?.interval || 1;
        break;
    }

    setRecurrenceConfig(newConfig);
  }, [formRecurrenceType, formStartDate, recurrenceConfig]);

  // 保存待办事项（使用 React Hook Form）
  const onSubmit = async (data: TodoFormData) => {
    // 如果是编辑模式，使用已加载的原始事项（如果是重复事项的实例）
    // 否则使用传入的 todo
    const todoToSave = originalTodo || todo;
    
    // 开始日期：用户勾选「无日期」则存 0；否则使用表单中的 formStartDate
    const startDateToUse = hasNoDate ? NO_DATE_START : formStartDate;
    const startDateValue = hasNoDate
      ? NO_DATE_START
      : (() => {
          const d = new Date(startDateToUse);
          d.setHours(0, 0, 0, 0);
          return d.getTime();
        })();

    const newTodo: Todo = {
      // 编辑模式使用原始 ID，新建模式生成新 ID
      id: todoToSave?.id || `todo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: data.title.trim(),
      description: data.description?.trim() || undefined,
      // 保留原有的完成状态，不通过表单修改
      completed: todoToSave?.completed || false,
      completedAt: todoToSave?.completedAt,
      startDate: startDateValue,
      // 根据重复频次类型设置结束日期
      // 只有选择"结束时间"时才设置 endDate
      // 选择"重复次数"时不设置 endDate，而是使用 maxOccurrences
      endDate: recurrenceConfig && data.recurrenceFrequencyType === 'endDate' && data.endDate
        ? new Date(data.endDate).setHours(23, 59, 59, 999)
        : recurrenceConfig && (data.recurrenceFrequencyType === 'count' || data.recurrenceFrequencyType === 'unlimited')
          ? undefined
          : todoToSave?.endDate,
      // 仅当用户主动设置重复时才写入 recurrence（默认不重复）
      recurrence: recurrenceConfig
        ? {
            ...recurrenceConfig,
            completedDates: todoToSave?.recurrence?.completedDates,
            maxOccurrences: data.recurrenceFrequencyType === 'count' ? data.recurrenceCount : undefined,
          }
        : undefined,
      reminder: data.reminderEnabled
        ? {
            enabled: true,
            minutesBefore: (data.reminderMinutes && data.reminderMinutes.length > 0) ? data.reminderMinutes : [-60], // 默认提前1小时
            methods: ['notification'],
          }
        : undefined,
      fontColor: data.fontColor !== '#000000' ? data.fontColor : undefined,
      // 分组协作字段（必须选择分组）
      groupId: data.selectedGroupId || (groups.length > 0 ? groups.find(g => g.isPersonal)?.id : undefined),
      assignedTo: (data.assignedTo && data.assignedTo.length > 0) ? data.assignedTo : undefined,
      createdBy: todoToSave?.createdBy || user?.id || undefined,
      // 编辑模式保留原始创建时间，新建模式使用当前时间
      createdAt: todoToSave?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    console.log('[TodoForm] 保存待办事项:', {
      isEditMode,
      todoId: todo?.id,
      originalTodoId: originalTodo?.id,
      newTodoId: newTodo.id,
      startDate: new Date(newTodo.startDate).toISOString(),
      endDate: newTodo.endDate ? new Date(newTodo.endDate).toISOString() : '无'
    });

    try {
      const saved = await saveTodo(newTodo);
      if (user) {
        markTodoUpdatedByUs(saved.id);
        queryClient.setQueryData<Todo[]>(['todos'], (old) => {
          if (!old) return old;
          const i = old.findIndex((t) => t.id === saved.id);
          if (i >= 0) return old.map((t, idx) => (idx === i ? saved : t));
          return [saved, ...old];
        });
      }
      if (data.selectedGroupId) {
        await setLastUsedGroupId(data.selectedGroupId);
      }
      onSave?.(saved);
      onClose();
    } catch (error) {
      console.error('保存待办事项失败:', error);
      alert('保存失败，请重试');
    }
  };

  // 更新删除菜单位置
  useEffect(() => {
    if (showDeleteDialog && deleteButtonRef) {
      const rect = deleteButtonRef.getBoundingClientRect();
      const menuWidth = 256; // w-64 = 256px
      const menuHeight = 220; // 估算菜单高度（3个选项 + padding）
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // 默认显示在按钮下方
      let top = rect.bottom + 8;
      let left = rect.left;
      
      // 如果下方空间不足，显示在按钮上方
      if (top + menuHeight > viewportHeight) {
        top = rect.top - menuHeight - 8;
      }
      
      // 如果右侧空间不足，调整左侧位置
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 16;
      }
      
      // 确保不超出左侧
      if (left < 16) {
        left = 16;
      }
      
      setDeleteMenuPosition({
        top,
        left,
      });
    } else {
      setDeleteMenuPosition(null);
    }
  }, [showDeleteDialog, deleteButtonRef]);

  // 删除待办事项
  const handleDelete = async (deleteType?: 'current' | 'current-and-future' | 'all') => {
    // 使用原始事项进行删除操作
    const todoToDelete = originalTodo || todo;
    if (!todoToDelete) return;
    
    // 如果没有重复配置，直接删除
    if (!todoToDelete.recurrence) {
      if (!confirm(`确定要删除待办事项"${todoToDelete.title}"吗？`)) {
        return;
      }
      try {
        await deleteTodo(todoToDelete.id);
        if (user) {
          queryClient.setQueryData<Todo[]>(['todos'], (old) =>
            old ? old.filter((t) => t.id !== todoToDelete.id) : old
          );
        }
        onClose();
      } catch (error) {
        console.error('删除待办事项失败:', error);
        alert('删除失败，请重试');
      }
      return;
    }

    // 如果有重复配置，显示删除选择对话框
    if (!deleteType) {
      setShowDeleteDialog(true);
      return;
    }

    try {
      await getTodos(); // 确保数据已加载
      
      console.log('[TodoForm] 删除待办事项:', {
        todoId: todo?.id,
        originalTodoId: todoToDelete.id,
        todoTitle: todoToDelete.title,
        hasRecurrence: !!todoToDelete.recurrence,
        deleteType
      });
      
      // 使用已加载的原始事项
      const originalTodoForDelete = todoToDelete;
      
      if (!originalTodoForDelete) {
        console.error('[TodoForm] 找不到原始待办事项');
        alert('找不到待办事项');
        return;
      }
      
      console.log('[TodoForm] 找到原始待办事项:', {
        id: originalTodoForDelete.id,
        title: originalTodoForDelete.title,
        startDate: new Date(originalTodoForDelete.startDate).toISOString(),
        endDate: originalTodoForDelete.endDate ? new Date(originalTodoForDelete.endDate).toISOString() : null
      });

      // 使用传入的 todo 的日期（实例日期），而不是原始事项的日期
      if (!todo) {
        console.error('[TodoForm] todo 未定义');
        return;
      }
      const currentDate = new Date(todo.startDate);
      currentDate.setHours(0, 0, 0, 0);
      const currentTimestamp = currentDate.getTime();

      if (deleteType === 'current') {
        // 删除当前事项：将当前日期添加到例外日期列表
        const exceptionDates = originalTodoForDelete.recurrence?.exceptionDates || [];
        
        // 如果当前日期不在例外日期列表中，添加它
        if (!exceptionDates.includes(currentTimestamp)) {
          const updatedRecurrence = {
            ...originalTodoForDelete.recurrence!,
            exceptionDates: [...exceptionDates, currentTimestamp],
          };
          
          const updatedTodo: Todo = {
            ...originalTodoForDelete,
            recurrence: updatedRecurrence,
            updatedAt: Date.now(),
          };
          
          console.log('[TodoForm] 删除当前事项 - 添加到例外日期:', {
            currentDate: currentDate.toISOString(),
            exceptionDates: updatedRecurrence.exceptionDates.map((ts: number) => new Date(ts).toISOString())
          });
          
          const saved = await saveTodo(updatedTodo);
          if (user) {
            queryClient.setQueryData<Todo[]>(['todos'], (old) => {
              if (!old) return old;
              const i = old.findIndex((t) => t.id === saved.id);
              if (i >= 0) return old.map((t, idx) => (idx === i ? saved : t));
              return [saved, ...old];
            });
          }
        } else {
          console.log('[TodoForm] 删除当前事项 - 日期已在例外列表中');
        }
      } else if (deleteType === 'current-and-future') {
        // 删除当前及未来事项：更新原始待办事项的结束日期为当前日期前一天
        const dayBefore = new Date(currentDate);
        dayBefore.setDate(dayBefore.getDate() - 1);
        dayBefore.setHours(23, 59, 59, 999);
        
        const updatedTodo: Todo = {
          ...originalTodoForDelete,
          endDate: dayBefore.getTime(),
          updatedAt: Date.now(),
        };
        
        console.log('[TodoForm] 删除当前及未来事项:', {
          oldEndDate: originalTodoForDelete.endDate ? new Date(originalTodoForDelete.endDate).toISOString() : '无',
          newEndDate: dayBefore.toISOString()
        });
        
        const saved = await saveTodo(updatedTodo);
        if (user) {
          queryClient.setQueryData<Todo[]>(['todos'], (old) => {
            if (!old) return old;
            const i = old.findIndex((t) => t.id === saved.id);
            if (i >= 0) return old.map((t, idx) => (idx === i ? saved : t));
            return [saved, ...old];
          });
        }
      } else if (deleteType === 'all') {
        // 删除全部事项：删除原始待办事项
        console.log('[TodoForm] 删除全部事项:', originalTodoForDelete.id);
        await deleteTodos([originalTodoForDelete.id]);
        if (user) {
          queryClient.setQueryData<Todo[]>(['todos'], (old) =>
            old ? old.filter((t) => t.id !== originalTodoForDelete.id) : old
          );
        }
      }
      
      console.log('[TodoForm] 删除操作完成');
      setShowDeleteDialog(false);
      onSave?.(originalTodoForDelete, { deleted: true }); // 触发保存回调，局部移除该项
      onClose();
    } catch (error) {
      console.error('删除待办事项失败:', error);
      alert('删除失败，请重试');
    }
  };

  // 格式化日期为 YYYY-MM-DD
  const formatDateInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 解析日期输入
  const parseDateInput = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // 折叠区块标题摘要（有非默认值时显示）
  const recurrenceReminderSummary = [
    recurrenceConfig ? getRecurrenceTypeLabel(formRecurrenceType || 'daily', recurrenceConfig) : null,
    formReminderEnabled ? '已设提醒' : null,
  ].filter(Boolean).join(' · ') || null;
  const advancedSummary = [
    (formAssignedTo || []).length > 0 ? `已分配 ${(formAssignedTo || []).length} 人` : null,
    formFontColor && formFontColor !== '#000000' ? '自定义颜色' : null,
  ].filter(Boolean).join(' · ') || null;

  const CollapsibleSection = ({
    id,
    title,
    summary,
    expanded,
    onToggle,
    children,
  }: {
    id: string;
    title: string;
    summary?: string | null;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
  }) => (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          {title}
          {summary && (
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({summary})</span>
          )}
        </span>
        <svg
          className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div id={`${id}-content`} aria-labelledby={`${id}-header`} className="px-4 pb-4 pt-0 space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return createPortal(
    <>
      {/* 遮罩 */}
      <div
        className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
        onClick={(_e) => {
          closeAllDropdowns();
          onClose();
        }}
      />

      {/* 表单模态框 */}
      <div
        className={`fixed inset-0 z-50 flex ${isMobile ? 'items-end' : 'items-center justify-center'} ${isMobile ? '' : 'p-4'}`}
        onClick={(e) => {
          e.stopPropagation();
          // 点击模态框空白区域时关闭所有下拉列表
          if (e.target === e.currentTarget) {
            closeAllDropdowns();
          }
        }}
      >
        <div className={`glass w-full ${isMobile ? 'max-h-[95vh] rounded-t-2xl' : 'max-w-2xl max-h-[90vh] rounded-2xl'} overflow-y-auto overflow-x-hidden p-6`}>
          {/* 头部 */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/50">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <h2 className="text-xl font-semibold text-foreground">
                {isEditMode ? '编辑待办事项' : '新增待办事项'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-accent/80 transition-colors duration-200 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 表单内容 */}
          <div className="space-y-4">
            {/* 基本信息：标题、描述、无日期、日期、分组 */}
            <CollapsibleSection
              id="basic"
              title="基本信息"
              expanded={expandedBasic}
              onToggle={() => setExpandedBasic((e) => !e)}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('title')}
                  placeholder="请输入标题"
                  className={`ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full ${isMobile ? 'text-base py-3' : ''}`}
                  autoFocus={!isMobile}
                />
                <FormError error={errors.title?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  描述
                </label>
                <textarea
                  {...register('description')}
                  placeholder="请输入待办事项描述（可选）"
                  rows={3}
                  className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full resize-none"
                />
                <FormError error={errors.description?.message} />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="todo-no-date"
                  checked={hasNoDate}
                  onChange={(e) => setHasNoDate(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-ios-blue cursor-pointer"
                />
                <label htmlFor="todo-no-date" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                  无日期（仅显示在待办列表，不显示在日历中）
                </label>
              </div>
              {!hasNoDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    日期
                  </label>
                  <input
                    type="date"
                    value={formatDateInput(formStartDate)}
                    onChange={(e) => setFormStartDate(parseDateInput(e.target.value))}
                    className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  所属分组 <span className="text-red-500">*</span>
                </label>
                {groups.length > 0 ? (
                  <div className="relative">
                    <button
                      ref={groupButtonRef}
                      onClick={() => toggleDropdown('group')}
                      className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full text-left flex items-center justify-between"
                      type="button"
                    >
                      <span className="flex items-center gap-2">
                        {formSelectedGroupId ? (
                          <>
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: groups.find(g => g.id === formSelectedGroupId)?.color || '#3B82F6' }}
                            />
                            <span>{groups.find(g => g.id === formSelectedGroupId)?.name || '请选择分组'}</span>
                          </>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">请选择分组</span>
                        )}
                      </span>
                      <svg
                        className={`w-5 h-5 transition-transform ${openDropdown === 'group' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdown === 'group' && dropdownPosition && createPortal(
                      <div 
                        className="fixed bg-white dark:bg-gray-800 rounded-ios-lg shadow-ios-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto z-[60]"
                        style={{
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                        }}
                      >
                        {groups.map((group) => (
                          <button
                            key={group.id}
                            onClick={() => {
                              setValue('selectedGroupId', group.id);
                              setValue('assignedTo', []);
                              closeAllDropdowns();
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                              formSelectedGroupId === group.id ? 'bg-ios-blue/10 text-ios-blue' : ''
                            }`}
                          >
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: group.color || '#3B82F6' }}
                            />
                            <span className="text-sm">{group.name}</span>
                            {formSelectedGroupId === group.id && (
                              <svg className="w-4 h-4 ml-auto text-ios-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>,
                      document.body
                    )}
                  </div>
                ) : (
                  <div className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-3 w-full flex items-center justify-center text-gray-500 dark:text-gray-400 min-h-[44px]">
                    <span className="text-sm">加载分组中...</span>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* 重复与提醒 */}
            <CollapsibleSection
              id="recurrence-reminder"
              title="重复与提醒"
              summary={recurrenceReminderSummary}
              expanded={expandedRecurrenceReminder}
              onToggle={() => setExpandedRecurrenceReminder((e) => !e)}
            >
              {/* 重复设置区域 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    重复设置
                  </label>
                  <div className="space-y-3">
                    {/* 重复类型选择 */}
                    <div className="relative">
                      <button
                        ref={recurrenceButtonRef}
                        onClick={() => toggleDropdown('recurrence')}
                        className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full text-left flex items-center justify-between"
                        type="button"
                      >
                        <span>
                          {recurrenceConfig
                            ? getRecurrenceTypeLabel(formRecurrenceType || 'daily', recurrenceConfig)
                            : '不重复'}
                        </span>
                        <svg
                          className={`w-5 h-5 transition-transform ${openDropdown === 'recurrence' ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {openDropdown === 'recurrence' && dropdownPosition && createPortal(
                        <div 
                          className="fixed bg-white dark:bg-gray-800 rounded-ios-lg shadow-ios-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto z-[60]"
                          style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                          }}
                        >
                          <button
                            onClick={() => {
                              setValue('recurrenceType', undefined);
                              setRecurrenceConfig(undefined);
                              closeAllDropdowns();
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between border-b border-gray-200 dark:border-gray-600 ${
                              !recurrenceConfig ? 'bg-ios-blue/10 text-ios-blue' : ''
                            }`}
                          >
                            <span>不重复</span>
                            {!recurrenceConfig && (
                              <svg className="w-4 h-4 text-ios-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          {recurrenceOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => {
                                setValue('recurrenceType', option.value);
                                closeAllDropdowns();
                              }}
                              className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                                formRecurrenceType === option.value ? 'bg-ios-blue/10 text-ios-blue' : ''
                              }`}
                            >
                              <span>{option.label}</span>
                              {formRecurrenceType === option.value && (
                                <svg className="w-4 h-4 text-ios-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>

                    {/* 重复频次选择（仅在选择重复类型后显示） */}
                    {recurrenceConfig && (
                    <div className="relative">
                      <button
                        ref={frequencyButtonRef}
                        onClick={() => toggleDropdown('frequency')}
                        className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full text-left flex items-center justify-between cursor-pointer"
                        type="button"
                      >
                        <span>
                          {formRecurrenceFrequencyType === 'unlimited' && '无限制'}
                          {formRecurrenceFrequencyType === 'endDate' && '结束时间'}
                          {formRecurrenceFrequencyType === 'count' && `重复 ${formRecurrenceCount} 次`}
                        </span>
                        <svg
                          className={`w-5 h-5 transition-transform ${openDropdown === 'frequency' ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {openDropdown === 'frequency' && dropdownPosition && createPortal(
                        <div 
                          className="fixed glass rounded-xl max-h-48 overflow-y-auto z-[60]"
                          style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                          }}
                        >
                          <button
                            onClick={() => {
                              setValue('recurrenceFrequencyType', 'unlimited');
                              closeAllDropdowns();
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                              formRecurrenceFrequencyType === 'unlimited' ? 'bg-ios-blue/10 text-ios-blue' : ''
                            }`}
                          >
                            <span>无限制</span>
                            {formRecurrenceFrequencyType === 'unlimited' && (
                              <svg className="w-4 h-4 text-ios-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setValue('recurrenceFrequencyType', 'endDate');
                              closeAllDropdowns();
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                              formRecurrenceFrequencyType === 'endDate' ? 'bg-ios-blue/10 text-ios-blue' : ''
                            }`}
                          >
                            <span>结束时间</span>
                            {formRecurrenceFrequencyType === 'endDate' && (
                              <svg className="w-4 h-4 text-ios-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setValue('recurrenceFrequencyType', 'count');
                              closeAllDropdowns();
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                              formRecurrenceFrequencyType === 'count' ? 'bg-ios-blue/10 text-ios-blue' : ''
                            }`}
                          >
                            <span>重复次数</span>
                            {formRecurrenceFrequencyType === 'count' && (
                              <svg className="w-4 h-4 text-ios-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </div>,
                        document.body
                      )}

                      {/* 根据选择的频次类型显示相应的输入控件 */}
                      {formRecurrenceFrequencyType === 'endDate' && (
                        <div className="mt-3 relative">
                          <input
                            type="date"
                            value={formEndDate ? formatDateInput(formEndDate) : ''}
                            onChange={(e) =>
                              setValue('endDate', e.target.value ? parseDateInput(e.target.value) : null)
                            }
                            className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full pr-10"
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      )}

                      {formRecurrenceFrequencyType === 'count' && (
                        <div className="mt-3">
                          <input
                            type="number"
                            min="1"
                            value={formRecurrenceCount}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              setValue('recurrenceCount', Math.max(1, value));
                            }}
                            className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-full"
                            placeholder="请输入重复次数"
                          />
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                </div>

                {/* 重复类型配置 */}
                {recurrenceConfig && (
                  <div className="pl-4 border-l-2 border-ios-blue/30 space-y-3 mt-3">
                    {/* 周重复配置 */}
                    {formRecurrenceType === 'weekly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          选择星期
                        </label>
                        <div className="grid grid-cols-7 gap-2">
                          {['日', '一', '二', '三', '四', '五', '六'].map((day, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                const newConfig = { ...recurrenceConfig, weekday: index };
                                setRecurrenceConfig(newConfig);
                              }}
                              className={`py-2 rounded-lg text-sm transition-colors ${
                                recurrenceConfig.weekday === index
                                  ? 'bg-ios-blue text-white'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 自定义间隔配置 */}
                    {(formRecurrenceType === 'custom-daily' ||
                      formRecurrenceType === 'custom-weekly' ||
                      formRecurrenceType === 'custom-monthly' ||
                      formRecurrenceType === 'custom-yearly') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          间隔
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={recurrenceConfig.interval || 1}
                            onChange={(e) => {
                              setRecurrenceConfig({
                                ...recurrenceConfig,
                                interval: parseInt(e.target.value) || 1,
                              });
                            }}
                            className="ios-input bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-ios-blue/20 focus:border-ios-blue transition-colors w-24"
                          />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formRecurrenceType === 'custom-daily' && '天'}
                            {formRecurrenceType === 'custom-weekly' && '周'}
                            {formRecurrenceType === 'custom-monthly' && '月'}
                            {formRecurrenceType === 'custom-yearly' && '年'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 提醒设置 */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={formReminderEnabled}
                    onChange={(e) => setValue('reminderEnabled', e.target.checked)}
                    className="ios-checkbox"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">启用提醒</span>
                </label>
                {formReminderEnabled && (
                  <div className="pl-6">
                    <div className="flex flex-wrap gap-2">
                      {[-1440, -60, -30, -15, -5].map((minutes) => {
                        const hours = Math.abs(Math.floor(minutes / 60));
                        const mins = Math.abs(minutes % 60);
                        const label =
                          minutes < -60
                            ? `提前${hours}小时${mins > 0 ? `${mins}分钟` : ''}`
                            : `提前${Math.abs(minutes)}分钟`;
                        return (
                          <button
                            key={minutes}
                            onClick={() => {
                              const currentMinutes = formReminderMinutes || [];
                              if (currentMinutes.includes(minutes)) {
                                setValue('reminderMinutes', currentMinutes.filter((m) => m !== minutes));
                              } else {
                                setValue('reminderMinutes', [...currentMinutes, minutes].sort((a, b) => a - b));
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              (formReminderMinutes || []).includes(minutes)
                                ? 'bg-ios-blue text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* 高级选项：分配给、字体颜色 */}
            <CollapsibleSection
              id="advanced"
              title="高级选项"
              summary={advancedSummary}
              expanded={expandedAdvanced}
              onToggle={() => setExpandedAdvanced((e) => !e)}
            >
              {selectedGroupId && groupMembers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    分配给
                  </label>
                  <div className="space-y-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                    {groupMembers.map((member) => (
                      <label key={member.id} className="flex items-center gap-3 cursor-pointer py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 -mx-2 transition-colors">
                        <input
                          type="checkbox"
                          checked={(formAssignedTo || []).includes(member.userId)}
                          onChange={(e) => {
                            const currentAssignedTo = formAssignedTo || [];
                            if (e.target.checked) {
                              setValue('assignedTo', [...currentAssignedTo, member.userId]);
                            } else {
                              setValue('assignedTo', currentAssignedTo.filter(id => id !== member.userId));
                            }
                          }}
                          className="ios-checkbox"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                          {member.username || member.email || '未知用户'}
                        </span>
                      </label>
                    ))}
                  </div>
                  {(formAssignedTo || []).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      未分配时，所有分组成员都可以完成此任务
                    </p>
                  )}
                  {(formAssignedTo || []).length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      只有被分配的用户可以完成此任务
                    </p>
                  )}
                </div>
              )}
              <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                字体颜色
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {presetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => {
                      setValue('fontColor', color);
                      closeAllDropdowns();
                    }}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      formFontColor === color
                        ? 'border-ios-blue scale-110 shadow-md'
                        : 'border-gray-300 dark:border-gray-600 hover:border-ios-blue/50 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
                <button
                  onClick={() => toggleDropdown('color')}
                  className="w-8 h-8 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-ios-blue flex items-center justify-center transition-colors"
                  title="自定义颜色"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>
              {openDropdown === 'color' && (
                <div className="mt-3">
                  <input
                    type="color"
                    value={formFontColor}
                    onChange={(e) => setValue('fontColor', e.target.value)}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
              )}
              </div>
            </CollapsibleSection>

            {/* 创建者信息（编辑模式） */}
            {isEditMode && todo?.createdBy && (
              <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                创建者: {todo.createdBy === user?.id ? '我' : '其他成员'}
              </div>
            )}
          </div>

          {/* 底部操作按钮 */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            {isEditMode && (
              <button
                ref={setDeleteButtonRef}
                onClick={() => handleDelete()}
                className="text-sm text-red-600 dark:text-red-400 hover:underline flex items-center gap-1"
              >
                删除任务
                {todo?.recurrence && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
            )}
            {!isEditMode && <div />}
            
            <div className="flex gap-2 ml-auto">
              <button onClick={onClose} className="ios-btn-secondary">
                取消
              </button>
              <button onClick={handleSubmit(onSubmit as (data: TodoFormData) => void)} className="ios-btn-primary">
                保存
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 删除选项菜单 - 使用 portal 渲染到 body，浮在表单上方 */}
      {showDeleteDialog && todo && todo.recurrence && deleteMenuPosition && createPortal(
        <>
          <div
            className="fixed inset-0 z-[100]"
            onClick={() => setShowDeleteDialog(false)}
          />
          <div
            className="fixed w-64 glass rounded-xl z-[101] max-h-[80vh] overflow-y-auto"
            style={{
              top: `${deleteMenuPosition.top}px`,
              left: `${deleteMenuPosition.left}px`,
            }}
          >
            <div className="p-2">
              <div className="mb-2 px-2 py-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  选择删除方式：
                </p>
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => handleDelete('current')}
                  className="w-full text-left px-3 py-2 rounded-ios hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">删除当前事项</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    只删除当前日期对应的这一项
                  </div>
                </button>

                <button
                  onClick={() => handleDelete('current-and-future')}
                  className="w-full text-left px-3 py-2 rounded-ios hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">删除当前及未来事项</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    删除当前日期及之后的所有重复项
                  </div>
                </button>

                <button
                  onClick={() => handleDelete('all')}
                  className="w-full text-left px-3 py-2 rounded-ios hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm border-t border-gray-200 dark:border-gray-700 mt-1 pt-2"
                >
                  <div className="font-medium text-red-600 dark:text-red-400">删除全部事项</div>
                  <div className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                    删除所有日期的重复项
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>,
    document.body
  );
};

export default TodoForm;
